import { compareAsc } from "date-fns";
import { DateTime } from "luxon";
import { COMPETITORS_KEYWORD_TRACKER_NAME } from "../constants/shared.js";
import { InterviewAgentType, ProjectMode } from "../entities/project.js";
import { isComplexQuestionType, isSurveyScale } from "../entities/question.js";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath
} from "./adminAppRoutes.js";
function assertUnreachable(value, fallback) {
  try {
    throw new Error(`Unexpected value provided: ${JSON.stringify(value)}`);
  } catch (error) {
    if (fallback) {
      const returnValue = fallback();
      if (process.env.NODE_ENV !== "production") {
        throw error;
      }
      return returnValue;
    }
    throw error;
  }
}
function isDefined(value) {
  return typeof value !== "undefined";
}
function deepFreeze(value) {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const nestedValue of Object.values(value)) {
      deepFreeze(nestedValue);
    }
    Object.freeze(value);
  }
  return value;
}
function levenshteinDistance(left, right) {
  let previousRow = Array.from(
    { length: right.length + 1 },
    (_, index) => index
  );
  left.forEach((leftItem, leftIndex) => {
    const currentRow = [leftIndex + 1];
    right.forEach((rightItem, rightIndex) => {
      currentRow.push(
        Math.min(
          (currentRow[rightIndex] ?? 0) + 1,
          (previousRow[rightIndex + 1] ?? 0) + 1,
          (previousRow[rightIndex] ?? 0) + (leftItem === rightItem ? 0 : 1)
        )
      );
    });
    previousRow = currentRow;
  });
  return previousRow.at(-1) ?? 0;
}
const extractDomainFromEmail = (email) => {
  const parts = email.split("@");
  if (parts.length === 2 && parts[1]) {
    return parts[1];
  }
  return "";
};
function convertToValidHttpsUrl(url) {
  const urlWithHttps = url.startsWith("https://") ? url : `https://${url}`;
  const pattern = /^https:\/\/[^\s/$.?#].[^\s]*$/i;
  return pattern.test(urlWithHttps) ? urlWithHttps : null;
}
function isValidDomain(domain) {
  if (!domain.includes(".")) {
    return false;
  }
  const pattern = /^[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
  if (pattern.test(domain)) {
    const parts = domain.split(".");
    for (const part of parts) {
      if (part.startsWith("-") || part.endsWith("-")) {
        return false;
      }
    }
    return true;
  }
  return false;
}
const isValidEmail = (email, excludeAliases) => {
  if (!email.includes("@")) {
    return false;
  }
  const [localPart, domainPart] = email.split("@");
  if (!domainPart) return false;
  if (excludeAliases && localPart?.includes("+")) return false;
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
};
const ALPHARUN_EMAIL_DOMAIN = "@alpharun.com";
function isAlpharunEmail(email) {
  return !!email && email.toLowerCase().endsWith(ALPHARUN_EMAIL_DOMAIN);
}
function checkIsValidInterviewEmail(email) {
  const includesAliases = isAlpharunEmail(email);
  return isValidEmail(email, !includesAliases);
}
function isComplexProjectQuestionInsert(question) {
  return !("id" in question) && "type" in question && isComplexQuestionType(question.type);
}
function isComplexProjectQuestion(question) {
  return "id" in question && "type" in question && isComplexQuestionType(question.type);
}
function isComplexInterviewQuestion(question) {
  return "id" in question;
}
function isValidBasicQuestion(question) {
  return typeof question === "object" && question !== null && "question" in question && typeof question.question === "string" && question.question.trim() !== "";
}
function isValidComplexQuestion(question, allowInsert) {
  const isComplexQuestion = isComplexProjectQuestion(question) || allowInsert && isComplexProjectQuestionInsert(question);
  if (!isComplexQuestion) return false;
  const hasValidOptions = Array.isArray(question.options) && question.options.length > 0 && question.options.every(
    (option) => typeof option.name === "string" && option.name.trim() !== ""
  );
  const hasValidScale = question.type !== "rating" || typeof question.scale === "string" && isSurveyScale(question.scale);
  const hasValidType = isComplexQuestionType(
    question.type
  );
  return hasValidOptions && hasValidScale && hasValidType;
}
function splitProjectQuestions(questions) {
  const basicQuestions = [];
  const complexQuestions = [];
  questions.forEach((question) => {
    if (isComplexProjectQuestion(question) || isComplexProjectQuestionInsert(question)) {
      complexQuestions.push(question);
    } else {
      basicQuestions.push(question);
    }
  });
  return { basicQuestions, complexQuestions };
}
function buildInterviewContactDisplayNames(contact, accountName) {
  return buildContactDisplayNames(
    "Interview participant",
    contact,
    accountName
  );
}
function buildTrainingContactDisplayNames(contact) {
  return buildContactDisplayNames("Training participant", contact, void 0);
}
const UNKNOWN_CONTACT_DISPLAY_NAME = "Unknown contact";
function buildPersonName(contact) {
  const name = [contact?.first_name, contact?.last_name].filter(isNotNullNotUndefined).filter(checkIsNotEmpty).join(" ");
  return name.length > 0 ? name : void 0;
}
function buildContactDisplayNames(fallbackName, contact, accountName) {
  const name = buildPersonName(contact);
  const email = contact.email;
  const phoneNumber = contact.phone_number;
  const extId = contact.ext_id;
  const names = [name, email, phoneNumber].filter(isNotNullNotUndefined).filter(checkIsNotEmpty);
  const primaryDisplayName = names.at(0) || extId || fallbackName;
  const secondaryDisplayName = names.at(1) || null;
  const tertiaryDisplayName = names.at(2) || null;
  return {
    primaryDisplayName: accountName ? `${primaryDisplayName} (${accountName})` : primaryDisplayName,
    secondaryDisplayName,
    tertiaryDisplayName
  };
}
const getShareLinkForInterview = ({
  baseUrl,
  projectId,
  interviewId,
  primaryDisplayName,
  transcriptFragmentId,
  isInFountainIntegration
}) => {
  const projectUrl = buildProjectUrlPath(
    projectId,
    ProjectDetailsTabPath.Interviews,
    interviewId
  );
  const url = new URL(projectUrl, baseUrl);
  url.searchParams.append("n", primaryDisplayName);
  if (transcriptFragmentId) url.searchParams.append("f", transcriptFragmentId);
  if (isInFountainIntegration) url.searchParams.append("int", "fountain");
  return url.toString();
};
function splitSummaryTextIntoLines(summary) {
  return summary.split(". ").map(
    (sentence, index, array) => `- ${sentence.trim()}${index === array.length - 1 ? "" : "."}`
  );
}
const isCompanyBlocked = (company) => {
  if (company.billing_current_plan) return false;
  if (company.trial_expires_at && /* @__PURE__ */ new Date() < new Date(company.trial_expires_at))
    return false;
  return true;
};
const getInterviewDurationEstimate = (numQuestions, options) => {
  if (numQuestions <= 1)
    return `one ${options?.shouldAbbreviate ? "min" : "minute"}`;
  if (options?.shouldAbbreviate) return `${numQuestions + 1} min`;
  return `${numQuestions + 1} minute${options?.shouldUseSingularPhrasing ? "" : "s"}`;
};
const isGlobalCompetitiveKeywordTracker = (keywordTracker) => {
  return keywordTracker.type === "lexical" && keywordTracker.name === COMPETITORS_KEYWORD_TRACKER_NAME;
};
function reorderArrayItems(items, index, direction) {
  if (direction === "up" && index === 0 || direction === "down" && index === items.length - 1) {
    return false;
  }
  const swapIndex = index + (direction === "up" ? -1 : 1);
  if (swapIndex < 0 || swapIndex >= items.length) {
    return [...items];
  }
  const newItems = [...items];
  const currentItem = newItems[index];
  newItems[index] = newItems[swapIndex];
  newItems[swapIndex] = currentItem;
  return newItems;
}
function getDisplayPhoneNumber(phoneNumber, extension) {
  return extension ? `${phoneNumber} ext. ${extension}` : phoneNumber;
}
function getDialingPhoneNumber(phoneNumber, extension) {
  return `${phoneNumber.replace(/\s/g, "")}${extension ? `,${extension}` : ""}`;
}
function maybeGetValidUrl(url) {
  if (!isNaN(Number(url))) {
    return null;
  }
  if (url.startsWith("http://")) {
    return null;
  }
  const urlWithProtocol = url.startsWith("https://") ? url : `https://${url}`;
  try {
    const parsedUrl = new URL(urlWithProtocol);
    if (parsedUrl.protocol !== "https:") {
      return null;
    }
    if (!parsedUrl.hostname.includes(".") || parsedUrl.hostname.endsWith(".")) {
      return null;
    }
    const parts = parsedUrl.hostname.split(".");
    const lastPart = parts[parts.length - 1];
    if (lastPart === void 0 || lastPart.length < 2) {
      return null;
    }
    return parsedUrl;
  } catch (error) {
    return null;
  }
}
function isValidUrl(url) {
  return maybeGetValidUrl(url) !== null;
}
function slugifyString(name) {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}
function isRecruitingModeProject(project) {
  const agentType = "settings" in project ? project.settings.interview_agent_type : project.interview_agent_type;
  return project.mode === ProjectMode.VOICE_AGENT && agentType === InterviewAgentType.RecruitingInterviewer;
}
function getIsTrainingProject(project) {
  const agentType = "settings" in project ? project.settings.interview_agent_type : project.interview_agent_type;
  return agentType === InterviewAgentType.Training;
}
function getIsRecruitingOrTrainingProject(project) {
  return isRecruitingModeProject(project) || getIsTrainingProject(project);
}
function isNotNullNotUndefined(value) {
  return value !== null && value !== void 0;
}
function isNotUndefined(value) {
  return value !== void 0;
}
function checkIsNotEmpty(value) {
  return value.length > 0;
}
function hasItems(arr) {
  return arr != null && arr.length > 0;
}
function areAllMembersUndefined(array) {
  return array.every((element) => !isNotUndefined(element));
}
function getRandomIntInclusive(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1) + min);
}
function extractUniqueIdsFromEntities(items) {
  return [...new Set(items.map((item) => item.id))];
}
function safeParseJSON(blob) {
  try {
    return JSON.parse(blob);
  } catch (error) {
    return null;
  }
}
function isoStringDateRangeToTimestampRange(isoStringDateRange) {
  return {
    startTimestamp: new Date(isoStringDateRange.start).getTime(),
    endTimestamp: new Date(isoStringDateRange.end).getTime()
  };
}
function timestampRangeToISOStringDateRange(timestampRange) {
  return {
    start: new Date(timestampRange.startTimestamp).toISOString(),
    end: new Date(timestampRange.endTimestamp).toISOString()
  };
}
function compareISOStringDateRangesByStartAsc(isoStringDateRangeA, isoStringDateRangeB) {
  return compareISOStringDatesAsc(
    isoStringDateRangeA.start,
    isoStringDateRangeB.start
  );
}
function compareISOStringDatesAsc(isoStringDateA, isoStringDateB) {
  return compareAsc(new Date(isoStringDateA), new Date(isoStringDateB));
}
function formatDateUTC(dateString, formatString) {
  const dt = DateTime.fromISO(dateString, { zone: "utc" });
  if (!dt.isValid) {
    throw new Error(`Invalid ISO date: ${dateString}`);
  }
  return dt.toFormat(formatString);
}
function findMostRecentEntityCreatedAt(entities) {
  if (entities.length === 0) {
    return void 0;
  }
  const [mostRecent] = entities.toSorted(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  return mostRecent?.created_at;
}
function renderPlural(count, variants) {
  if (count === 0) {
    return variants.zero ?? variants.other(count);
  }
  if (count === 1) {
    return variants.one;
  }
  if (count === 2) {
    return variants.two ?? variants.other(count);
  }
  return variants.other(count);
}
function timestampToTranscriptTimestampFormat(timestamp) {
  const totalSeconds = Math.floor(timestamp / 1e3);
  const sign = totalSeconds < 0 ? "-" : "";
  const absSeconds = Math.abs(totalSeconds);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor(absSeconds % 3600 / 60);
  const seconds = absSeconds % 60;
  const pad = (n) => n.toString().padStart(2, "0");
  return `[${sign}${pad(hours)}:${pad(minutes)}:${pad(seconds)}]`;
}
function transcriptTimestampFormatToTimestamp(transcriptTimestamp) {
  const match = transcriptTimestamp.match(/^\[(-?)(\d+):(\d+):(\d+)\]$/);
  if (!match) {
    return null;
  }
  const [, sign, hours, minutes, seconds] = match;
  if (hours === void 0 || minutes === void 0 || seconds === void 0) {
    return null;
  }
  const hoursNum = parseInt(hours, 10);
  const minutesNum = parseInt(minutes, 10);
  const secondsNum = parseInt(seconds, 10);
  if (minutesNum > 59 || secondsNum > 59) {
    return null;
  }
  const totalMs = hoursNum * 3600 * 1e3 + minutesNum * 60 * 1e3 + secondsNum * 1e3;
  return sign === "-" ? -totalMs : totalMs;
}
function computeMedian(values) {
  if (values.length === 0) {
    return 0;
  }
  const sorted = values.toSorted((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    const lower = sorted[middle - 1];
    const upper = sorted[middle];
    if (lower === void 0 || upper === void 0) {
      return 0;
    }
    return (lower + upper) / 2;
  }
  return sorted[middle] ?? 0;
}
function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
function recordEntries(obj) {
  return Object.entries(obj);
}
function checkIsFreeOfControlCharacters(value) {
  return ![...value].some(
    (char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127
  );
}
export {
  UNKNOWN_CONTACT_DISPLAY_NAME,
  areAllMembersUndefined,
  assertUnreachable,
  buildInterviewContactDisplayNames,
  buildPersonName,
  buildTrainingContactDisplayNames,
  capitalize,
  checkIsFreeOfControlCharacters,
  checkIsNotEmpty,
  checkIsValidInterviewEmail,
  compareISOStringDateRangesByStartAsc,
  compareISOStringDatesAsc,
  computeMedian,
  convertToValidHttpsUrl,
  deepFreeze,
  extractDomainFromEmail,
  extractUniqueIdsFromEntities,
  findMostRecentEntityCreatedAt,
  formatDateUTC,
  getDialingPhoneNumber,
  getDisplayPhoneNumber,
  getInterviewDurationEstimate,
  getIsRecruitingOrTrainingProject,
  getIsTrainingProject,
  getRandomIntInclusive,
  getShareLinkForInterview,
  hasItems,
  isAlpharunEmail,
  isCompanyBlocked,
  isComplexInterviewQuestion,
  isComplexProjectQuestion,
  isComplexProjectQuestionInsert,
  isDefined,
  isGlobalCompetitiveKeywordTracker,
  isNotNullNotUndefined,
  isNotUndefined,
  isRecruitingModeProject,
  isValidBasicQuestion,
  isValidComplexQuestion,
  isValidDomain,
  isValidEmail,
  isValidUrl,
  isoStringDateRangeToTimestampRange,
  levenshteinDistance,
  maybeGetValidUrl,
  recordEntries,
  renderPlural,
  reorderArrayItems,
  safeParseJSON,
  slugifyString,
  splitProjectQuestions,
  splitSummaryTextIntoLines,
  timestampRangeToISOStringDateRange,
  timestampToTranscriptTimestampFormat,
  transcriptTimestampFormatToTimestamp
};
//# sourceMappingURL=utilities.js.map
